import { useEffect, useRef, useState } from "react";
import { ArrowDownload20Regular, Dismiss20Regular } from "@fluentui/react-icons";

/** Um documento que o viewer sabe mostrar: o nome (título e download) e como ir buscar o ficheiro. */
export interface ViewerDocument {
  name: string;
  load: () => Promise<Blob>;
}

export type ViewerKind = "pdf" | "image" | "other";

/** O que o browser consegue mostrar: PDF e imagens. O tipo do ficheiro manda; quando vem genérico
 * (anexos de email guardados como octet-stream), decide a extensão do nome. */
export function viewerKind(name: string, contentType: string): ViewerKind {
  const type = contentType.toLowerCase();
  if (type === "application/pdf" || /\.pdf$/i.test(name)) return "pdf";
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(name)) {
    return "image";
  }
  return "other";
}

/** O erro em texto de gente: o corpo de um erro do servidor costuma ser um ProblemDetails JSON, e
 * mostrava-se cru. Reconhece os erros das várias APIs pelo `status`, sem depender de nenhuma. */
function errorMessage(reason: unknown): string {
  const status = (reason as { status?: unknown } | null)?.status;
  if (status === 401) return "Sessão expirada. Autentique-se novamente.";
  if (status === 403) return "Sem permissões para ver este documento.";
  if (status === 404) return "O documento já não existe.";
  if (reason instanceof Error && reason.message) {
    try {
      const parsed = JSON.parse(reason.message);
      if (parsed && typeof parsed === "object") {
        return parsed.detail || parsed.title || "Não foi possível abrir o documento.";
      }
    } catch {
      // Não é JSON — usa o texto tal como veio.
    }
    return reason.message;
  }
  return "Não foi possível abrir o documento.";
}

interface DocumentViewerProps {
  document: ViewerDocument;
  onClose: () => void;
}

/** Mostra um documento dentro da app, por cima do que estiver aberto, em vez de o descarregar:
 * validar faturas uma a uma enchia as transferências e custava vários cliques por documento. */
export function DocumentViewer({ document: viewed, onClose }: DocumentViewerProps) {
  const [file, setFile] = useState<{ url: string; kind: ViewerKind } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropPressRef = useRef(false);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setFile(null);
    setError(null);

    viewed
      .load()
      .then((blob) => {
        if (cancelled) return;
        const kind = viewerKind(viewed.name, blob.type);
        // O leitor de PDF do browser só abre um blob que se diga PDF.
        const typed =
          kind === "pdf" && blob.type !== "application/pdf"
            ? new Blob([blob], { type: "application/pdf" })
            : blob;
        url = URL.createObjectURL(typed);
        setFile({ url, kind });
      })
      .catch((reason) => {
        if (cancelled) return;
        setError(errorMessage(reason));
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [viewed]);

  // Esc fecha só o viewer e o Tab fica preso nos botões dele: apanhados em captura e travados
  // aqui, para as janelas por baixo (que também fecham com Esc e prendem o Tab) não reagirem. O
  // foco volta a quem o abriu.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      event.stopPropagation();
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>("button:not([disabled])");
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey, true);
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey, true);
      opener?.focus?.();
    };
  }, []);

  const download = () => {
    if (!file) return;
    const anchor = document.createElement("a");
    anchor.href = file.url;
    anchor.download = viewed.name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      // Acima das janelas da página (a de revisão vai a 60).
      style={{ backgroundColor: "rgba(0,0,0,0.5)", padding: "0.75rem", zIndex: 70 }}
      onMouseDown={(event) => {
        backdropPressRef.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (backdropPressRef.current && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Documento ${viewed.name}`}
        className="bg-card shadow-lg w-full overflow-hidden flex flex-col"
        style={{ borderRadius: "var(--radius)", maxWidth: "64rem", height: "95vh" }}
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-4">
          <h2 className="text-sm font-medium text-foreground break-all">{viewed.name}</h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30 disabled:opacity-50"
              style={{ color: "var(--primary)" }}
              onClick={download}
              disabled={!file}
            >
              <ArrowDownload20Regular className="w-4 h-4" />
              Descarregar
            </button>
            <button
              ref={closeRef}
              type="button"
              aria-label="Fechar"
              className="p-1 rounded hover:bg-accent/30 text-secondary-text"
              onClick={onClose}
            >
              <Dismiss20Regular className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, backgroundColor: "var(--muted)" }}>
          {error ? (
            <div
              role="alert"
              className="flex items-center justify-center text-center p-6 text-sm"
              style={{ width: "100%", height: "100%", color: "var(--destructive)" }}
            >
              {error}
            </div>
          ) : !file ? (
            <div
              aria-live="polite"
              className="flex items-center justify-center text-sm text-secondary-text"
              style={{ width: "100%", height: "100%" }}
            >
              A abrir…
            </div>
          ) : file.kind === "pdf" ? (
            <iframe
              title={`Documento ${viewed.name}`}
              // #navpanes=0 esconde o painel lateral de miniaturas das páginas.
              src={`${file.url}#navpanes=0`}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : file.kind === "image" ? (
            <div
              className="overflow-auto flex items-start justify-center"
              style={{ width: "100%", height: "100%" }}
            >
              <img src={file.url} alt={viewed.name} style={{ maxWidth: "100%" }} />
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center gap-2 text-center p-6 text-secondary-text text-sm"
              style={{ width: "100%", height: "100%" }}
            >
              <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                Este tipo de ficheiro não tem pré-visualização
              </span>
              <span>Use Descarregar, em cima, para o abrir no computador.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
